import { useState } from 'react';
import {
    Typography,
    InputAdornment,
    FormControl,
    TextField,
    Button,
    Autocomplete,
    OutlinedInput,
    IconButton
} from '@mui/material';
import { Analytics } from 'lib/api';
import { Box } from '@mui/system';
import { CountryCodes } from '../CountryCodes';
import { isUsaZip } from 'helpers/zips';
import { KYBStepBanner } from './KYBStepBanner';
import { KYX_ANALYTICS_EVENTS } from '../StartKYX';
import { makeStyles } from '@mui/styles';
import { US_POSTAL_CODE_REGEX } from 'helpers/constants';
import CloseIcon from '@mui/icons-material/Close';
import validator from 'validator';

const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    phoneBox: {
        marginTop: '2em'
    },
    phoneInput: {
        width: '50%'
    },
    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important',
        background: '#1BABE2!important'
    },
    contactInforFormLabel: {
        color: '#225384',
        fontWeight: 600
    },
    businessAdressBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    formControl: {
        width: '100%!important'
    },
    informationOptions: {
        marginTop: '5em',
        display: 'flex',
        justifyContent: 'flex-end'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    },
    inputFormControl: {
        '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            '-moz-appearance': 'none',
            appearance: 'none',
            margin: 0
        }
    }
});

const legalNamePattern = new RegExp(/^[a-zA-Z0-9_\s]*$/);

export const KYBFirstStep = ({ handleClose, incrementPage, activePage, handleInputChange, formValues, setFormValues }) => {
    const classes = useStyles();
    const [errorPostal, setErrorPostal] = useState(false);
    const [errorWebsite, setErrorWebsite] = useState(false);
    const [errorLegalName, setErrorLegalName] = useState(false);

    const incrementStep = () => {};

    const validate = () => {
        if (isUsaZip(formValues.postalCode) && US_POSTAL_CODE_REGEX.test(formValues.postalCode)) {
            setErrorPostal(false);
        } else {
            setErrorPostal(true);
            return;
        }

        if (!legalNamePattern.test(formValues.legal_name)) {
            setErrorLegalName(true);
            return;
        }
        if (errorLegalName) {
            setErrorLegalName(false);
        }

        let res = validator.isURL(formValues.website_url);
        if (res) {
            const body = {
                step: KYX_ANALYTICS_EVENTS.STEP_4,
                eventData: formValues
            };
            Analytics.createKYXEvent(body).catch(error => console.error(error));
            incrementPage();
        }
    };

    const validatePostalCode = () => {
        let postalCode = formValues.postalCode;

        if (isUsaZip(postalCode) && US_POSTAL_CODE_REGEX.test(formValues.postalCode)) {
            setErrorPostal(false);
        } else {
            setErrorPostal(true);
        }
    };

    const validateLegalName = () => {
        if (!formValues.legal_name) return;
        if (!legalNamePattern.test(formValues.legal_name)) {
            setErrorLegalName(true);
            return;
        }
        if (errorLegalName) {
            setErrorLegalName(false);
        }
    };

    const validateWebsite = () => {
        if (!formValues.website_url) return;
        let res = validator.isURL(formValues.website_url);
        if (!res) {
            setErrorWebsite(true);
            return;
        }
        if (errorWebsite) {
            setErrorWebsite(false);
        }
    };

    const enableNext = () => {
        return !(
            formValues.legal_name !== '' &&
            formValues.phone_number !== '' &&
            validator.isMobilePhone(formValues.phone_number, 'en-US') &&
            formValues.line1 !== '' &&
            formValues.city !== '' &&
            formValues.state !== '' &&
            formValues.website_url !== '' &&
            validator.isURL(formValues.website_url) &&
            isUsaZip(formValues.postalCode) &&
            legalNamePattern.test(formValues.legal_name) &&
            US_POSTAL_CODE_REGEX.test(formValues.postalCode)
        );
    };

    const handleChangeState = (e, newVal) => {
        setFormValues({
            ...formValues,
            state: newVal ? newVal.label : ''
        });
    };

    const codeToState = code => {
        if (!code) return null;
        return CountryCodes.filter(item => item.label === code)[0];
    };

    return (
        <Box className={classes.dialogBody}>
            <KYBStepBanner activeStep={activePage} incrementStep={() => incrementStep()} />
            <Box id="KybFirstStep" className={classes.content}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" className={classes.contentTitle}>
                            Business Contact Information
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: '#A8B6C1' }}>
                            This will help us verify your business quickly
                        </Typography>
                    </Box>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        sx={{ alignSelf: 'flex-start' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box marginTop="10px" flexDirection="column">
                    <FormControl className={classes.formControl}>
                        <Typography variant="subtitle2">Business Legal Name</Typography>
                        <TextField
                            error={!!errorLegalName}
                            id="legal_name"
                            placeholder="Enter business name"
                            variant="outlined"
                            size="small"
                            onInput={e => e.target.value}
                            onChange={handleInputChange}
                            className={classes.inputFormControl}
                            value={formValues.legal_name}
                            onBlur={validateLegalName}
                            helperText={errorLegalName ? 'Please type the legal name without special characters' : ''}
                        />
                    </FormControl>
                </Box>
                <Box marginTop="10px" flexDirection="column">
                    <FormControl className={classes.formControl}>
                        <Typography variant="subtitle2">Business Phone Number</Typography>
                        <OutlinedInput
                            id="phone_number"
                            size="small"
                            placeholder="Enter phone number"
                            variant="outlined"
                            type="number"
                            onInput={e => {
                                e.target.value = e.target.value.length > 10 ? e.target.value.slice(0, 10) : e.target.value;
                            }}
                            inputProps={{ maxLength: 10 }}
                            onChange={handleInputChange}
                            value={formValues.phone_number}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Typography> +1 </Typography>
                                </InputAdornment>
                            }
                            className={classes.inputFormControl}
                        />
                    </FormControl>
                </Box>
                <Box marginTop="10px" flexDirection="column">
                    <FormControl className={classes.formControl}>
                        <Typography variant="subtitle2">Business Address</Typography>
                        <Box className={classes.businessAdressBox}>
                            <FormControl style={{ gridColumn: '1/3' }}>
                                <TextField
                                    id="line1"
                                    placeholder="Street line 1"
                                    size="small"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    value={formValues.line1}
                                    className={classes.inputFormControl}
                                />
                            </FormControl>
                            <FormControl style={{ gridColumn: '1/3' }}>
                                <TextField
                                    id="line2"
                                    placeholder="Street line 2"
                                    size="small"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    value={formValues.line2}
                                    className={classes.inputFormControl}
                                />
                            </FormControl>
                            <FormControl>
                                <TextField
                                    id="city"
                                    placeholder="City"
                                    size="small"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    value={formValues.city}
                                    className={classes.inputFormControl}
                                />
                            </FormControl>
                            <FormControl>
                                <Autocomplete
                                    disablePortal
                                    id="state"
                                    autoSelect={true}
                                    options={CountryCodes}
                                    sx={{ gridColumn: '2/3' }}
                                    renderInput={params => <TextField {...params} placeholder="State" />}
                                    getOptionLabel={option => option.value}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChangeState}
                                    value={codeToState(formValues.state)}
                                />
                            </FormControl>
                            <FormControl>
                                <TextField
                                    error={!!errorPostal}
                                    id="postalCode"
                                    placeholder="Postal Code"
                                    size="small"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    onInput={e => {
                                        e.target.value = e.target.value.length > 5 ? e.target.value.slice(0, 5) : e.target.value;
                                    }}
                                    value={formValues.postalCode}
                                    onBlur={validatePostalCode}
                                    className={classes.inputFormControl}
                                    helperText={
                                        errorPostal ? 'This zip code is not valid, a 5-digit U.S. zip code is required.' : ''
                                    }
                                />
                            </FormControl>
                            <FormControl>
                                <TextField
                                    id="country"
                                    size="small"
                                    placeholder="USA"
                                    disabled
                                    variant="outlined"
                                    className={classes.inputFormControl}
                                />
                            </FormControl>
                        </Box>
                    </FormControl>
                </Box>
                <Box marginTop="20px" flexDirection="column">
                    <FormControl className={classes.formControl}>
                        <Typography variant="subtitle2">Business Website URL</Typography>
                        <TextField
                            error={errorWebsite}
                            id="website_url"
                            placeholder="Enter business url"
                            variant="outlined"
                            onBlur={validateWebsite}
                            onChange={handleInputChange}
                            value={formValues.website_url}
                            size="small"
                            className={classes.inputFormControl}
                            helperText={errorWebsite ? 'Please, enter a valid website address' : ''}
                        />
                    </FormControl>
                </Box>
                <Box className={classes.informationOptions}>
                    <Button variant="contained" disabled={enableNext()} onClick={() => validate()}>
                        Next
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
